import { Link } from 'react-router';
import { COMPANY } from '@/content/company';
import { Clause, LegalPage } from './LegalPage';
import legal from './LegalPage.module.css';

const TOC = [
  '1. Who we are',
  '2. Data we process',
  '3. Purposes & legal bases',
  '4. Retention',
  '5. Recipients',
  '6. Transfers',
  '7. Your rights',
  '8. Security',
  '9. Children’s data',
  '10. Changes',
] as const;

const LEGAL_BASES: readonly [string, string][] = [
  ['Responding to your enquiry', 'Art. 6(1)(b) - steps prior to entering a contract'],
  ['Performing an agreed project', 'Art. 6(1)(b) - performance of a contract'],
  /*
   * Keeping a dead enquiry on file for two months short of two years is not a
   * step prior to a contract - the step was taken when we replied. It is a
   * legitimate interest, and listing it as one closes a gap between this table
   * and the retention period in section 4, which used to rest on a basis this
   * table did not give it.
   */
  ['Following up on an earlier enquiry', 'Art. 6(1)(f) - legitimate interest'],
  ['Operating and securing the website', 'Art. 6(1)(f) - legitimate interest'],
  ['Protecting the contact form from automated abuse', 'Art. 6(1)(f) - legitimate interest'],
  /*
   * A separate row rather than an extension of the one above. The purposes differ
   * - one keeps junk out of an inbox, the other keeps a phone number out of a
   * harvester's list - and Art. 13(1)(c) wants the purpose of each operation, not
   * a category broad enough to cover both.
   */
  ['Keeping our phone number from being harvested', 'Art. 6(1)(f) - legitimate interest'],
  ['Accounting and tax records', 'Art. 6(1)(c) - legal obligation'],
];

/**
 * How many purposes in the table rest on legitimate interest, counted from the
 * table rather than written out.
 *
 * Art. 21(4) requires the right to object to be presented clearly, and § 7 does
 * that by pointing at the specific rows it applies to. It said "the three
 * purposes named in section 3" and there were four: a row was added when the
 * phone reveal shipped, and the sentence naming them was not. A reader counting
 * the rows would have found the policy miscounting its own table, on the one
 * paragraph the GDPR singles out for prominence.
 *
 * Deriving it means the next row cannot reintroduce that.
 */
const LEGITIMATE_INTEREST_COUNT = LEGAL_BASES.filter(([, basis]) =>
  basis.includes('6(1)(f)'),
).length;

const NUMBER_WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven'] as const;
const spellOut = (n: number): string => NUMBER_WORDS[n] ?? String(n);

const mail = <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>;

export function Privacy() {
  return (
    <LegalPage
      eyebrow="Personal data & GDPR"
      title="Privacy Policy"
      toc={TOC}
      intro={
        <>
          This policy explains how {COMPANY.legalName} processes personal data collected through
          skabro.eu, in accordance with Regulation (EU) 2016/679 (GDPR) and Slovak Act No. 18/2018
          Coll. on personal data protection.
        </>
      }
    >
      <Clause n={1} title="Who we are">
        <p className={legal.pairs}>
          The controller of your personal data is {COMPANY.legalName}, with registered office at{' '}
          {COMPANY.address.street}, {COMPANY.address.localityFormal}, {COMPANY.address.country}, IČO{' '}
          {COMPANY.ico}, registered in the Commercial Register of {COMPANY.register.court},{' '}
          {COMPANY.register.sectionEn}.
        </p>
        <p>
          For any question about this policy or about how we handle your data, write to {mail}. We
          have not appointed a Data Protection Officer, as we are not required to do so.
        </p>
      </Clause>

      <Clause n={2} title="Data we process">
        <p className={legal.runOn}>
          <strong>Contact form and email.</strong> When you contact us we process the name, company
          name, email address and any information you choose to include in your message. Providing
          this data is voluntary, but without an email address we cannot reply.
        </p>
        <p className={legal.runOn}>
          <strong>Technical data.</strong> Our hosting provider records standard server log data,
          including IP address, request time, requested resource, browser type and referring page.
          These logs are used to operate and secure the site.
        </p>
        <p>
          <strong>Where it comes from.</strong> We collect personal data directly from you when you
          write to us or use the form, and automatically from your device through server logs. We do
          not buy personal data and we do not obtain it from data brokers or public profiles.
        </p>
        <p className={legal.runOn}>
          <strong>Anti-abuse data on the contact page.</strong> The enquiry form and the &ldquo;show
          phone number&rdquo; button are both protected by Google reCAPTCHA, which collects
          information about your browser, device and interaction with the page in order to score how
          likely the request is to be automated, and sets a cookie in Google&rsquo;s domain. We
          store the resulting score with your enquiry. This happens on the contact page only, and
          only there because that is where both of those things are. If you would prefer not to
          involve Google, email us at {mail} instead - it reaches the same inbox.
        </p>
        {/*
         * The reveal is a new processing operation and gets said out loud rather
         * than folded into the paragraph above. Two hashed-IP counters exist on
         * this site now, they have different retentions, and a policy that
         * mentioned only the one attached to enquiries would be incomplete in a way
         * that is hard to spot from outside.
         */}
        <p className={legal.runOn}>
          <strong>Showing you our phone number.</strong> The number is not printed on the site; it
          is held by our form backend and sent to your browser when you ask for it. To stop that
          being used to harvest it, we count requests per visitor: your IP address is hashed -
          turned into an irreversible digest, never stored in its readable form - and that digest is
          kept with the reCAPTCHA score for <strong>24 hours</strong>, purely so the hourly limit
          can be enforced, then deleted automatically. Nothing else about the request is recorded:
          no browser, no device, no page. We cannot tell from it who asked, and we do not try.
        </p>
        {/*
         * Cookies and local storage named separately. The reCAPTCHA writes a
         * first-party `_grecaptcha` entry into local storage under skabro.eu when
         * a token is minted, which is not a cookie and not in Google's domain -
         * so a paragraph that said "the only cookie" and stopped there was
         * describing the smaller half. See the header comment in Cookies.tsx.
         */}
        <p>
          <strong>Cookies and device storage.</strong> This site sets no cookies of its own, on any
          page, and on eight of its nine pages stores nothing on your device at all. On the contact
          page the reCAPTCHA described above accounts for two things and nothing else: a cookie
          Google sets in its own domain, and one reCAPTCHA entry written into your browser&rsquo;s
          local storage under skabro.eu when a check actually runs. Neither is set, read or used by
          us. Both are itemised in our <Link to="/cookies">Cookie Policy</Link>.
        </p>
      </Clause>

      <Clause n={3} title="Purposes and legal bases">
        <div className={`${legal.table} ${legal.twoColumn}`}>
          <div className={legal.tableHead}>
            <span>Purpose</span>
            <span>Legal basis</span>
          </div>
          {LEGAL_BASES.map(([purpose, basis]) => (
            <div className={legal.tableRow} key={purpose}>
              <span>{purpose}</span>
              <span>{basis}</span>
            </div>
          ))}
        </div>
        {/* Art. 13(1)(d) asks for the interest itself, not only the article
            number. Several rows above cite 6(1)(f); this says what the interest
            actually is in each case, and the list below must name one interest
            per such row - see LEGITIMATE_INTEREST_COUNT. */}
        <p>
          Where we rely on legitimate interest, the interests we are pursuing are: keeping the site
          available and free of attack; keeping the contact form usable by preventing automated
          abuse of it; keeping our telephone number out of the bulk-harvested lists that make a
          published number unusable; and being able to pick up a conversation you started with us.
          In each case we considered whether the processing was proportionate to that interest and
          whether it overrides your own - which is why the follow-up window is finite, the
          anti-abuse checks run on one page rather than all nine, the hashed record behind the phone
          number is deleted after 24 hours, and none of it is used for analytics, advertising or
          measurement. You can object to any of it at any time under section 7.
        </p>
      </Clause>

      <Clause n={4} title="How long we keep it">
        <p className={legal.continues}>
          The periods below are the ones we hold ourselves to, and each is an outer limit rather
          than a target:
        </p>
        <ul>
          <li>
            <strong>Enquiries that do not lead to an engagement</strong> - up to 24 months from your
            last message, so we can follow up on the conversation, after which they are deleted.
          </li>
          <li>
            <strong>Data related to a contract</strong> - for the duration of the contract, and
            afterwards for as long as Slovak accounting and tax legislation requires, which for
            accounting records is ten years from the end of the accounting period they concern.
          </li>
          <li>
            <strong>The reCAPTCHA score</strong> stored with a submission - deleted with the enquiry
            it belongs to, under whichever of the two periods above applies. It has no life of its
            own. What Google keeps at its end is governed by Google&rsquo;s policy, not ours.
          </li>
          <li>
            <strong>The hashed record behind a phone-number request</strong> - 24 hours, deleted
            automatically. It exists only to enforce the hourly limit on how often the number can be
            requested, and it is pruned by the same operation that serves the next request, so there
            is no scheduled job that could quietly stop running.
          </li>
          <li>
            <strong>Submissions our anti-abuse checks flag as automated</strong> - within the same
            24 months, and usually far less. They are stored rather than discarded, and not emailed
            on, so that a wrongly flagged message can be found and answered instead of vanishing
            silently. That is the only reason they are kept at all.
          </li>
          <li>
            <strong>Server logs</strong> - held by our hosting provider under its own retention
            schedule, which is short and set by them rather than by us. We do not copy them into any
            system of ours, we do not use them for anything but serving and securing the site, and
            we do not combine them with the enquiry data above. If you need the provider&rsquo;s
            exact period in order to make a request, write to {mail} and we will get it for you.
          </li>
        </ul>
      </Clause>

      <Clause n={5} title="Who receives your data">
        <p className={legal.runOn}>
          We do not sell personal data, we do not share it for advertising, and we do not send
          marketing or newsletter emails. Data may be processed on our behalf by service providers
          acting as processors - website hosting, form handling, email delivery, payment processing
          and, where relevant, accounting services - each bound by a data processing agreement. Data
          may also be disclosed to public authorities where we are legally required to do so.
        </p>
        {/*
         * Art. 13(1)(e) permits categories, and the paragraph above gives them.
         * Naming the two processors that actually hold your data costs nothing,
         * and it is the question a client's procurement review asks first. No
         * payment provider is named because none is engaged yet - naming a
         * plausible one would be inventing a fact about where your data goes,
         * which is the specific failure this policy exists to avoid.
         */}
        <p>
          Two of those are worth naming, because they are the ones that actually hold what you send
          us: the site is hosted by <strong>Netlify</strong>, and enquiries submitted through the
          form are stored in a <strong>Supabase</strong> database hosted in Frankfurt. Both act on
          our instructions under a data processing agreement. If we engage a payment provider for an
          invoice, it will be a regulated one and it will process card details on its own systems -
          we never see them.
        </p>
        <p>
          <strong>Google is a separate case, and worth stating plainly.</strong> The reCAPTCHA data
          described in section 2 goes to Google LLC, which uses it for its own purposes as well as
          ours - improving the service and its general abuse detection. Google is therefore not
          merely a processor acting on our instructions, and its handling of that data is governed
          by the{' '}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
            Google Privacy Policy
          </a>{' '}
          rather than by this one. This is the only place on the site where that is true, and it
          applies only if you use the contact form.
        </p>
      </Clause>

      <Clause n={6} title="Transfers outside the EEA">
        <p>
          We prefer providers that process data within the European Economic Area, and choose them
          that way where there is a reasonable option - the database holding form submissions is
          hosted in Frankfurt for that reason. Some of the infrastructure this site depends on -
          website hosting, email delivery and the reCAPTCHA protecting the contact form - is
          supplied by companies established outside the EEA, principally in the United States, so
          some processing takes place there.
        </p>
        <p>
          Where that happens, the transfer is covered by an adequacy decision of the European
          Commission or by the European Commission&rsquo;s Standard Contractual Clauses together
          with the supplementary measures those clauses require. We can tell you which mechanism
          applies to a particular provider on request - write to {mail}.
        </p>
      </Clause>

      <Clause n={7} title="Your rights">
        {/*
         * Art. 21(4) requires the right to object to be brought to the data
         * subject's attention "clearly and separately from any other
         * information". It used to sit as four words inside a list of six other
         * rights, which is the opposite of separately. Hence its own paragraph,
         * first, before the general list.
         */}
        <p className={legal.runOn}>
          <strong>Your right to object.</strong> Where we process your personal data on the basis of
          our legitimate interests - the {spellOut(LEGITIMATE_INTEREST_COUNT)} purposes named in
          section 3 - you have the right to object to that processing at any time, and to do so
          simply by telling us. Write to {mail} and say you object. We will then stop, unless we can
          show compelling legitimate grounds that override your interests, rights and freedoms; if
          we think we can, we will explain why rather than just continue. You do not need to give a
          reason and there is no cost.
        </p>
        <p className={legal.continues}>
          <strong>Your other rights.</strong> Under the GDPR you also have the right to access your
          data, to have inaccurate data corrected, to have data erased, to restrict processing, and
          to receive your data in a portable format. Where processing is based on consent, you may
          withdraw it at any time without affecting processing carried out beforehand.
        </p>
        <p>
          To exercise any of these rights, write to {mail}. We may ask you to confirm your identity
          before acting on a request. We answer within one month, as Article 12(3) of the GDPR
          requires - extendable by up to two further months where a request is complex or where we
          have received several from you at once. If that applies we will tell you inside the first
          month, with the reason.
        </p>
        <p>
          You also have the right to lodge a complaint with the Slovak supervisory authority,{' '}
          {COMPANY.dataProtectionAuthority.name}, {COMPANY.dataProtectionAuthority.address}, or with
          the supervisory authority in the EU or EEA country where you live or work.
        </p>
      </Clause>

      <Clause n={8} title="Security and automated decisions">
        <p>
          We apply appropriate technical and organisational measures to protect personal data,
          including encrypted transport, restricted access and regular review of the systems we use.
          We do not carry out automated decision-making or profiling that produces legal effects. No
          system can be guaranteed completely secure, and ordinary email is not a secure channel -
          please do not send credentials or confidential material to us by email before a
          confidentiality agreement is in place.
        </p>
      </Clause>

      <Clause n={9} title="Children’s data">
        <p>
          This site is aimed at businesses and is not directed at children. We do not knowingly
          collect personal data relating to a child. If you believe a child has sent us personal
          data, write to {mail} and we will delete it.
        </p>
      </Clause>

      <Clause n={10} title="Changes to this policy">
        <p>
          If this policy changes, the updated version will be published on this page with a new
          date, and that version applies from the date it is published. Where a change materially
          affects how we process your data and the law requires us to tell you directly, we will.
        </p>
      </Clause>
    </LegalPage>
  );
}
